import type {
  ChatSnapshot,
  ChatSummary as ChatSummaryView,
  MessageAuthor,
  MessageView,
  QueuedView,
  RequestView,
  RunView,
  SearchHit as SearchHitView,
  SearchResult as SearchResultView,
} from "@typedoc/contracts/chat";
import type { ChatState } from "@typedoc/contracts/chat/reducer";
import { ERROR_CATALOG, isErrorCode } from "@typedoc/contracts/errors";
import { plainText } from "./answer/plain-text";
import type {
  Author,
  Chat,
  ChatFile,
  ChatMessage,
  ChatRequest,
  ChatStep,
  ChatSummary,
  SearchHit,
  SearchResult,
  Starter,
} from "./types";

/**
 * The Chats API's views, drawn as the types the screens already read (`./types`). The screens have
 * one colleague to name (`tom`), so every person but the viewer is that colleague until the web
 * has a member list to resolve `ownerUserId` against.
 */

/**
 * A chat as the client holds it: the reducer's state, which is the newest page of the transcript
 * with its events folded in, and the older pages fetched before it (oldest first).
 */
export type Held = { state: ChatState; older: readonly MessageView[] };

const starterOf = (userId: string, me: string): Starter => (userId === me ? "me" : "tom");

const authorOf = (author: MessageAuthor, me: string): Author =>
  author.kind === "assistant" ? "assistant" : starterOf(author.userId, me);

export function toSummary(view: ChatSummaryView, me: string): ChatSummary {
  const starter = starterOf(view.ownerUserId, me);
  const { ask, answer } = view.peek;
  return {
    id: view.id,
    title: view.title,
    startedBy: starter,
    startedAt: new Date(view.startedAt),
    lastAt: new Date(view.lastAt),
    preview: view.preview,
    needsYou: view.needsYou,
    ...(view.archivedAt ? { archivedAt: new Date(view.archivedAt) } : {}),
    fileCount: view.fileCount,
    peek: {
      ...(ask
        ? { ask: { id: `${view.id}-ask`, author: starter, at: new Date(ask.at), text: ask.text } }
        : {}),
      // Abacor's words as they read, not the syntax that draws them: a widget is its label here.
      ...(answer
        ? {
            answer: {
              id: `${view.id}-answer`,
              author: "assistant" as const,
              at: new Date(answer.at),
              text: plainText(answer.text),
            },
          }
        : {}),
    },
  };
}

function toHit(hit: SearchHitView, me: string): SearchHit {
  return hit.kind === "message"
    ? {
        kind: "message",
        id: hit.id,
        at: new Date(hit.at),
        author: authorOf(hit.author, me),
        text: hit.text,
        asked: hit.asked,
      }
    : { kind: "step", id: hit.id, at: new Date(hit.at), app: hit.app, text: hit.text };
}

export function toSearchResult(view: SearchResultView, me: string): SearchResult {
  return {
    chat: toSummary(view.chat, me),
    titleMatch: view.titleMatch,
    hits: view.hits.map((hit) => toHit(hit, me)),
  };
}

/** What a message is drawn as, kept while the view of it is the same object the reducer holds. */
const drawn = new WeakMap<MessageView, { key: string; message: ChatMessage }>();

const requestOf = (view: RequestView): ChatRequest => ({
  id: view.id,
  kind: view.kind,
  prompt: view.prompt,
  status: view.status,
  canAnswer: view.canAnswer,
  response: view.response,
  ...(view.approvalId === undefined ? {} : { approvalId: view.approvalId }),
});

function toMessage(
  view: MessageView,
  me: string,
  asksViewer: boolean,
  request: RequestView | undefined,
): ChatMessage {
  // A request changes without its message changing (it is answered, or it lapses), so what is
  // kept is only as good as the request it was drawn with.
  const key = `${me}|${String(asksViewer)}|${
    request ? `${request.id}:${request.status}:${String(request.canAnswer)}` : ""
  }`;
  const kept = drawn.get(view);
  if (kept?.key === key) return kept.message;
  const message: ChatMessage = {
    id: view.id,
    author: authorOf(view.author, me),
    at: new Date(view.at),
    text: view.text,
    // Only the person a question is for is asked it; a request is pending while it is marked.
    ...(asksViewer && view.requestId !== undefined ? { asked: true } : {}),
    ...(view.status === "streaming" ? { streaming: true } : {}),
    ...(view.runId === undefined ? {} : { runId: view.runId }),
    ...(view.requestRunId === undefined ? {} : { requestRunId: view.requestRunId }),
    ...(view.files && view.files.length > 0 ? { files: view.files } : {}),
    ...(view.mentions && view.mentions.length > 0 ? { mentions: view.mentions } : {}),
    ...(view.steps.length > 0
      ? {
          steps: view.steps.map((step) => ({
            id: step.id,
            app: step.app,
            text: step.text,
            status: step.status,
            access: step.access,
            at: new Date(step.at),
          })),
        }
      : {}),
    ...(view.notes && view.notes.length > 0
      ? {
          notes: view.notes.map((note) => ({
            id: note.id,
            text: note.text,
            at: new Date(note.at),
          })),
        }
      : {}),
    ...(view.plan === undefined
      ? {}
      : { plan: { at: new Date(view.plan.at), items: view.plan.items } }),
    ...(view.sources && view.sources.length > 0
      ? { sources: view.sources.map((source) => ({ ...source, at: new Date(source.at) })) }
      : {}),
    ...(view.work && view.work.length > 0
      ? { work: view.work.map((line) => ({ ...line, at: new Date(line.at) })) }
      : {}),
    ...(view.writing === undefined ? {} : { writing: new Date(view.writing) }),
    ...(view.processChanges && view.processChanges.length > 0
      ? { processChanges: view.processChanges }
      : {}),
    ...(view.tableChanges && view.tableChanges.length > 0
      ? { tableChanges: view.tableChanges }
      : {}),
    ...(view.tries && view.tries.length > 0 ? { tries: view.tries } : {}),
    ...(request ? { request: requestOf(request) } : {}),
  };
  drawn.set(view, { key, message });
  return message;
}

/**
 * An Abacor message with nothing in it to show: cut off before a word, or never begun. One that
 * only did work (calls to connections), only made a note, only wrote a plan, only read, only
 * changed a draft or only asked has something to show.
 */
const isBlank = (view: MessageView) =>
  view.author.kind === "assistant" &&
  view.text === "" &&
  view.status !== "streaming" &&
  view.steps.length === 0 &&
  (view.notes ?? []).length === 0 &&
  view.plan === undefined &&
  (view.sources ?? []).length === 0 &&
  (view.work ?? []).length === 0 &&
  (view.processChanges ?? []).length === 0 &&
  (view.tableChanges ?? []).length === 0 &&
  (view.tries ?? []).length === 0 &&
  view.requestId === undefined;

const asPerson = (item: QueuedView): ChatMessage => ({
  id: item.id,
  author: "me",
  at: new Date(item.at),
  text: item.text,
  queued: true,
});

/**
 * Why the latest run ended without an answer: only a run that finished as failed has a code, and
 * the words are the catalog's. A code this build has no entry for (the API may be newer than the
 * page) reads as the general failure, so a person is never shown a code or a blank.
 */
function stoppedBy(run: RunView | null): Chat["stopped"] {
  if (run?.status !== "finished" || run.errorCode === undefined) return undefined;
  const code = isErrorCode(run.errorCode) ? run.errorCode : "INTERNAL_ERROR";
  return { code, message: ERROR_CATALOG[code].message };
}

/**
 * The whole chat. Two things are added to the transcript that the API does not list yet:
 * Abacor's answer before its first word (a run is working on a person's message, so the page
 * shows it answering), and what the person sent that waits behind that run, as theirs. When the
 * latest run has failed, the chat also says why (`stopped`).
 */
export function toChat({ state, older }: Held, me: string): Chat {
  const { snapshot } = state;
  const raw = [...older, ...snapshot.messages];
  const owner = snapshot.viewer === "owner";
  const requests = new Map(snapshot.requests.map((request) => [request.id, request]));
  const messages = raw
    .filter((view) => !isBlank(view))
    .map((view) =>
      toMessage(
        view,
        me,
        owner,
        view.requestId === undefined ? undefined : requests.get(view.requestId),
      ),
    );

  const { run } = snapshot;
  const last = raw.at(-1);
  // A run goes on after a turn of Abacor's that ended: nothing streams until the next call to the
  // model answers, and the page would say nothing through that wait. The last turn is marked, on a
  // copy, since the message drawn for a view is kept and read again.
  if (
    run !== null &&
    run.status !== "finished" &&
    last?.author.kind === "assistant" &&
    last.status === "complete"
  ) {
    const at = messages.findLastIndex((message) => message.id === last.id);
    const turn = messages[at];
    if (turn !== undefined) messages[at] = { ...turn, working: true };
  }
  if (
    run !== null &&
    run.status !== "finished" &&
    last?.author.kind === "person" &&
    !raw.some((view) => view.status === "streaming")
  ) {
    messages.push({
      id: `run-${run.id}`,
      author: "assistant",
      at: new Date(last.at),
      text: "",
      streaming: true,
    });
  }
  messages.push(...snapshot.queue.map(asPerson));

  const steps: ChatStep[] = raw.flatMap((view) =>
    view.steps.map((step) => ({
      id: step.id,
      at: new Date(step.at),
      app: step.app,
      text: step.text,
    })),
  );

  // Every file the loaded messages carry, once each, in the order they were sent.
  const files: ChatFile[] = [];
  for (const view of raw) {
    for (const file of view.files ?? []) {
      if (files.some((known) => known.id === file.id)) continue;
      files.push({
        id: file.id,
        name: file.name,
        at: new Date(view.at),
        mediaType: file.mediaType,
        size: file.size,
        // A file on an Abacor message is one it made; a person's own message carries what they sent.
        ...(view.author.kind === "assistant" ? { made: true as const } : {}),
      });
    }
  }

  const stopped = stoppedBy(run);
  const { chat } = snapshot;
  return {
    id: chat.id,
    title: chat.title,
    startedBy: starterOf(chat.ownerUserId, me),
    startedAt: new Date(chat.startedAt),
    lastAt: new Date(chat.lastAt),
    preview: chat.preview,
    needsYou: chat.needsYou,
    ...(chat.archivedAt ? { archivedAt: new Date(chat.archivedAt) } : {}),
    files,
    steps,
    messages,
    ...(run !== null && run.status !== "finished"
      ? {
          run: {
            id: run.id,
            stopping: run.stopping,
            ...(run.drafting ? { drafting: run.drafting } : {}),
          },
        }
      : {}),
    canAct: owner,
    ...(stopped ? { stopped } : {}),
  };
}

/**
 * What the owner's own actions change in a chat held here, ahead of the stream saying so. Each one
 * is something the stream will say again (`message`, `queue.changed`, the run's
 * events), and the reducer takes a repeat as nothing, so the cursor is left alone.
 */
function edited(held: Held, change: Partial<ChatSnapshot>): Held {
  return { ...held, state: { ...held.state, snapshot: { ...held.state.snapshot, ...change } } };
}

/** A message the owner sent that began a run at once. */
export function withStarted(held: Held, message: MessageView, runId: string): Held {
  const { messages, run } = held.state.snapshot;
  const before = messages.findIndex((existing) => existing.seq > message.seq);
  return edited(held, {
    messages: messages.some((existing) => existing.id === message.id)
      ? messages
      : before === -1
        ? [...messages, message]
        : [...messages.slice(0, before), message, ...messages.slice(before)],
    run: run?.id === runId ? run : { id: runId, status: "queued", stopping: false },
  });
}

/** A message the owner sent that waits behind the run that is working. */
export function withQueued(held: Held, item: QueuedView, runId: string): Held {
  const { queue, run } = held.state.snapshot;
  return edited(held, {
    queue: queue.some((existing) => existing.id === item.id) ? queue : [...queue, item],
    run: run?.id === runId ? run : { id: runId, status: "running", stopping: false },
  });
}

export const withoutQueued = (held: Held, id: string): Held =>
  edited(held, { queue: held.state.snapshot.queue.filter((item) => item.id !== id) });

/** The chat as the owner archived it or brought it back, from the summary the API answered with. */
export const withArchived = (held: Held, archivedAt: string | null): Held =>
  edited(held, { chat: { ...held.state.snapshot.chat, archivedAt } });

/**
 * The run as a command that may have changed it left it, or none. A command's answer does not say
 * which request the run carries on; when it is the run the page already knows, what the stream
 * said of it (`run.request`) stands, whichever arrived first.
 */
export function withRun(held: Held, run: RunView | null): Held {
  const known = held.state.snapshot.run;
  if (run === null || run.requestRunId !== undefined || known?.id !== run.id) {
    return edited(held, { run });
  }
  return edited(held, {
    run: known.requestRunId === undefined ? run : { ...run, requestRunId: known.requestRunId },
  });
}
